import { useState, useEffect } from 'react';

import { Container, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../components/Loader';
import Message from '../components/Message';
import { LinkContainer } from 'react-router-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';


import {
  useProfileMutation,
  useGetUserProDetailsQuery,
} from '../slices/usersApiSlice';
import { logout } from '../slices/authSlice';
import { toast } from 'react-toastify';

const ProfileScreen = () => {
  const [salutation, setSalutation] = useState('');
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [updateProfile, { isLoading: loadingUpdateProfile }] = useProfileMutation();

  const { userInfo } = useSelector((state) => state.auth);

  const {
    data: user,
    isLoading: isLoadingU,
    error: errorU,
  } = useGetUserProDetailsQuery(userInfo._Id);

  useEffect(() => {
    if (user) {
      setSalutation(user.salutation || '');
      setFirstname(user.firstname || '');
      setLastname(user.lastname || '');
    }
  }, [user]);

  const submitHandler = async (e) => {
    e.preventDefault();
    
    // Only validate password if trying to change it
    if (password || confirmPassword) {
      if (password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      if (password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
    }

    try {
      const updateData = {
        _id: userInfo._id,
        salutation,
        firstname,
        lastname,
      };

      // Only include password if it's being changed
      if (password) {
        updateData.password = password;
      }

      await updateProfile(updateData).unwrap();

      toast.success('Profile updated successfully');
      
      // Optionally logout and redirect to login
      // dispatch(logout());
      // navigate('/login?message=profile-updated');
      
      // Or just refresh the data
      window.location.reload(); // Simple refresh to show updated data
      
    } catch (err) {
      toast.error(err?.data?.message || err.error || 'Failed to update profile');
    }
  };

  if (isLoadingU) {
    return <Loader />;
  }

  if (errorU) {
    return <Message variant="danger">{errorU?.data?.message || errorU?.error || 'Error loading profile'}</Message>;
  }

  return (
    <Container fluid>
      <Row className='align-items-center mb-4'>
        <Col>
          <LinkContainer to='/profile'>
            <Button variant='primary' size='sm'>
              Profile
            </Button>
          </LinkContainer>
        </Col>
      </Row>

      <h2>My Profile</h2>

      {/* ── Link to pension scheme profile ── */}
      <Alert variant="info" className="mb-3">
        To update your pension scheme details — home address, civil status, date of birth,
        or email address — please use your{' '}
        <Link to="/sps-profile"><strong>Pension Scheme Profile</strong></Link>.
      </Alert>

      <Form onSubmit={submitHandler}>
        <Row>
          <Col md={6}>
            {/* User Information Section */}
            <div style={{ 
              border: '1px solid #ddd', 
              padding: '20px', 
              borderRadius: '10px', 
              backgroundColor: '#F0F8FF',
              marginBottom: '20px'
            }}>
              <h5 className="mb-3">Personal Information</h5>
              
              <Row>
                <Col sm={3}>
                  <Form.Group controlId="salutation">
                    <Form.Label>Title</Form.Label>
                    <Form.Control 
                      as="select" 
                      value={salutation} 
                      onChange={(e) => setSalutation(e.target.value)}
                    >
                      <option value="">Select Title</option>
                      <option value="Cllr.">Cllr.</option>
                      <option value="Mr.">Mr.</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Miss">Miss</option>
                      <option value="Dr.">Dr.</option>
                      <option value="Professor">Professor</option>
                      <option value="Mx">Mx</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group controlId="firstname">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control 
                      type="text" 
                      placeholder="Enter First Name" 
                      value={firstname} 
                      onChange={(e) => setFirstname(e.target.value)} 
                      required
                    />
                  </Form.Group>
                </Col>
                <Col sm={5}>
                  <Form.Group controlId="lastname">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control 
                      type="text" 
                      placeholder="Enter Last Name" 
                      value={lastname} 
                      onChange={(e) => setLastname(e.target.value)} 
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Change Password Section */}
            <div style={{ 
              border: '1px solid #ddd', 
              padding: '20px', 
              borderRadius: '10px', 
              backgroundColor: '#F0F8FF'
            }}>
              <h5 className="mb-3">Change Password</h5>
              
              <Form.Group className="mb-3" controlId="password">
                <Form.Label>New Password</Form.Label>
                <Form.Control 
                  type="password" 
                  placeholder="Enter new password (optional)" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Leave blank to keep current password. Minimum 6 characters.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3" controlId="confirmPassword">
                <Form.Label>Confirm New Password</Form.Label>
                <Form.Control 
                  type="password" 
                  placeholder="Confirm new password" 
                  value={confirmPassword} 
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </Form.Group>
            </div>
          </Col>
        </Row>

        <Button 
          type="submit" 
          variant="primary" 
          className="mt-3"
          disabled={loadingUpdateProfile}
        >
          {loadingUpdateProfile ? 'Updating...' : 'Update Profile'}
        </Button>
      </Form>
    </Container>
  );
};

export default ProfileScreen;